// V6-4-1 / ADR-0040: Reference Architecture v1 — 15 slots
//
// Each slot represents one capability a compliant AI system deployment is
// expected to fill.  The list, fit rules, effort bands, and dependency
// edges are the editorial content of reference_architecture_versions
// ra_version='v1'.  The PRD §7.1 table is the source.
//
// This file is pure data — no runtime, no imports outside the core type.
/**
 * Reference Architecture v1 — 15 slots covering the full governance
 * loop from source control to AI inventory discovery.
 */
export const REFERENCE_ARCHITECTURE_V1 = [
    {
        slot_id: 'source_control',
        role: 'Source control and code review — evidence provenance and governance repo hosting',
        capability_requirements: [
            'Git-based version control',
            'Branch protection with required reviews',
            'Audit log of merges and approvals',
        ],
        known_implementations: [
            'GitHub',
            'GitLab',
            'Azure DevOps Repos',
            'Bitbucket',
        ],
        effort_band: 'xs',
        depends_on: [],
    },
    {
        slot_id: 'ci_cd',
        role: 'CI/CD orchestration — the pipeline that gates releases on policy evaluation and evidence collection',
        capability_requirements: [
            'Triggered pipeline on push/PR',
            'Step-level pass/fail with exit codes',
            'Artifact upload and caching',
            'Secret injection without exposure',
        ],
        known_implementations: [
            'GitHub Actions',
            'GitLab CI',
            'Azure Pipelines',
            'Jenkins',
            'Argo Workflows',
            'CircleCI',
        ],
        effort_band: 's',
        depends_on: ['source_control'],
    },
    {
        slot_id: 'policy_engine',
        role: 'Policy engine — evaluates compiled policy bundles (OPA Rego, Kyverno, Cedar) in the pipeline or at admission',
        capability_requirements: [
            'Evaluate OPA Rego v1 bundles',
            'Return structured pass/fail per rule',
            'Support external data input',
        ],
        known_implementations: [
            'OPA / Gatekeeper',
            'Kyverno',
            'Cedar',
            'Styra DAS',
        ],
        effort_band: 'm',
        depends_on: ['ci_cd'],
    },
    {
        slot_id: 'signing_provenance',
        role: 'Signing and provenance — produce and verify attestations on artifacts (DSSE, Sigstore, SLSA)',
        capability_requirements: [
            'Generate in-toto attestations',
            'Sigstore keyless or BYO KMS signing',
            'Rekor transparency log integration',
            'SLSA Build L2+ provenance',
        ],
        known_implementations: [
            'Sigstore / cosign',
            'SLSA GitHub generator',
            'Notation (CNCF)',
            'AWS Signer',
        ],
        effort_band: 'm',
        depends_on: ['ci_cd'],
    },
    {
        slot_id: 'artifact_registry',
        role: 'Artifact and model registry — stores versioned models, containers, and packages with metadata',
        capability_requirements: [
            'Versioned artifact storage',
            'Metadata and lineage tracking',
            'Access control per artifact',
        ],
        known_implementations: [
            'MLflow',
            'Weights & Biases',
            'Amazon SageMaker Model Registry',
            'Vertex AI Model Registry',
            'Azure ML',
            'Hugging Face Hub',
            'OCI / Docker registries',
        ],
        effort_band: 'm',
        depends_on: [],
    },
    {
        slot_id: 'evaluation_harness',
        role: 'Evaluation harness — run model and system evaluations that produce evidence for control checks',
        capability_requirements: [
            'Automated eval suite execution',
            'Structured result output (SARIF, JUnit, or proprietary)',
            'Version-pinned eval configs',
        ],
        known_implementations: [
            'promptfoo',
            'Ragas',
            'DeepEval',
            'Inspect AI',
            'custom pytest suites',
        ],
        effort_band: 'm',
        depends_on: ['ci_cd'],
    },
    {
        slot_id: 'telemetry',
        role: 'Telemetry — runtime observability for drift detection and evidence collection',
        capability_requirements: [
            'Trace and metric collection (OTel preferred)',
            'GenAI semantic conventions support',
            'Alerting on drift thresholds',
        ],
        known_implementations: [
            'OpenTelemetry Collector',
            'Langfuse',
            'Datadog',
            'Arize',
            'Weights & Biases Prompts',
        ],
        effort_band: 'l',
        depends_on: [],
    },
    {
        slot_id: 'secrets_kms',
        role: 'Secrets and KMS — manage signing keys, API credentials, and encryption keys',
        capability_requirements: [
            'Hardware-backed key storage',
            'Rotation and audit logging',
            'Access policy per key',
        ],
        known_implementations: [
            'AWS KMS',
            'Azure Key Vault',
            'GCP Cloud KMS',
            'HashiCorp Vault',
        ],
        effort_band: 's',
        depends_on: [],
    },
    {
        slot_id: 'identity',
        role: 'Identity — user and service identity for ownership, sign-off, and RBAC',
        capability_requirements: [
            'SSO / SAML / OIDC',
            'Group and role management',
            'OAuth grant visibility for AI SaaS',
        ],
        known_implementations: [
            'Okta',
            'Microsoft Entra ID',
            'Google Workspace',
            'WorkOS',
        ],
        effort_band: 's',
        depends_on: [],
    },
    {
        slot_id: 'runtime_enforcement',
        role: 'Runtime enforcement — intercept or gate AI system calls at inference time',
        capability_requirements: [
            'Inline or sidecar interception',
            'Policy-driven allow/deny/log',
            'Decision log export as evidence',
        ],
        known_implementations: [
            'Zenity',
            'Pillar Security',
            'Arthur AI Shield',
            'WitnessAI',
            'custom API gateway rules',
        ],
        effort_band: 'xl',
        depends_on: ['policy_engine', 'identity'],
    },
    {
        slot_id: 'grc_platform',
        role: 'GRC platform of record — OSCAL export target, finding management, audit-evidence hub',
        capability_requirements: [
            'Receive OSCAL component definitions and assessment results',
            'Finding lifecycle management',
            'Evidence attachment and retention',
        ],
        known_implementations: [
            'Jira (with GRC workflow)',
            'ServiceNow GRC',
            'Archer',
            'Anecdotes',
            'Vanta',
            'Drata',
        ],
        effort_band: 'l',
        depends_on: [],
    },
    {
        slot_id: 'data_governance',
        role: 'Data governance and lineage — track training data provenance, classification, and retention',
        capability_requirements: [
            'Data catalog with lineage',
            'Classification labels on datasets',
            'Retention policy enforcement',
        ],
        known_implementations: [
            'Microsoft Purview',
            'Collibra',
            'Atlan',
            'OpenMetadata',
            'custom lineage in feature stores',
        ],
        effort_band: 'xl',
        depends_on: [],
    },
    {
        slot_id: 'human_review',
        role: 'Human-review workflow — structured sign-off and approval evidence for release and risk decisions',
        capability_requirements: [
            'Approval workflow with role gating',
            'Timestamped sign-off records',
            'Integration with ticketing or release tools',
        ],
        known_implementations: [
            'Jira (approval schemes)',
            'ServiceNow',
            'GitHub PR reviews',
            'PagerDuty (escalation)',
        ],
        effort_band: 's',
        depends_on: ['identity'],
    },
    {
        slot_id: 'sbom_inventory',
        role: 'SBOM and component inventory — ingest and maintain software and AI component bills of materials',
        capability_requirements: [
            'CycloneDX and/or SPDX ingest',
            'Component delta detection',
            'License classification',
        ],
        known_implementations: [
            'FedSec /api/ci/boms (native)',
            'Dependency-Track',
            'Snyk',
            'Anchore / Grype',
            'Trivy',
        ],
        effort_band: 'm',
        depends_on: ['ci_cd'],
    },
    {
        slot_id: 'ai_discovery',
        role: 'AI inventory discovery — detect AI tools and services in use across the organisation',
        capability_requirements: [
            'OAuth grant scanning (IdP)',
            'Network/DNS-based detection',
            'Employee-reported tool intake',
        ],
        known_implementations: [
            'FedSec discovered_tools (native)',
            'Zylo',
            'Productiv',
            'Torii',
            'Nudge Security',
        ],
        effort_band: 's',
        depends_on: ['identity'],
    },
];
/** Number of slots in v1. */
export const REFERENCE_ARCHITECTURE_V1_SLOT_COUNT = REFERENCE_ARCHITECTURE_V1.length;
//# sourceMappingURL=reference-architecture-v1.js.map